<template>
    <side-panel id="settings-panel" class="tiny-panel left-aligned settings-page">
        <template #head>
            <div class="a-header justify-content-between pt-4">
                <h3 class="panel-title">{{ __('settings') }}</h3>
                <div class="close-btn" @click="goBack">
                    <i class="fa-solid fa-xmark"></i>
                </div>
            </div>
        </template>
        <template #body>
            <div class="settings-wrapper">
                <geo-fleet-settings
                    :default-tab="activeTabIndex"
                    @tab-change="onTabChange"
                    @resetSettings="resetConfig"
                />
            </div>
        </template>
    </side-panel>
</template>

<script>
import SidePanel from '@/components/panels/SidePanel.vue'
import GeoFleetSettings from '@/components/panels/SettingsPanel/GeoFleetSettings.vue'
import { mapMutations } from 'vuex'
import { mapConfig } from '@/composables/useConfig'

// Order matches the <b-tab> order in GeoFleetSettings.vue — the array
// position is the b-tabs v-model index. Adding a tab here means adding
// the matching <b-tab> at the same position there.
const TABS = [
    { key: 'general',       permission: null },
    { key: 'vehicle-list',  permission: null },
    { key: 'map',           permission: null },
    { key: 'poi',           permission: ['use', 'poi'] },
    { key: 'driver-status', permission: null },
]

export default {
    name: 'SettingsPage',
    components: { SidePanel, GeoFleetSettings },
    computed: {
        ...mapConfig(['vehicleListModeView']),
        permittedTabs() {
            return TABS.filter(tab => !tab.permission || this.$can(...tab.permission))
        },
        activeTabIndex() {
            return this.findPermittedTab(this.$route.query.menu)?.index ?? 0
        }
    },
    created() {
        if (!this.findPermittedTab(this.$route.query.menu)) {
            this.$router.replace({ name: 'geofleet.settings', query: { menu: 'general' } })
        }
    },
    methods: {
        ...mapMutations(['expandPanel']),
        findPermittedTab(key) {
            const index = TABS.findIndex(t => t.key === key)
            if (index < 0 || !this.permittedTabs.includes(TABS[index])) {
                return null
            }
            return { tab: TABS[index], index }
        },
        onTabChange(index) {
            const tab = TABS[index]
            if (tab && tab.key !== this.$route.query.menu) {
                this.$router.replace({ name: 'geofleet.settings', query: { menu: tab.key } })
            }
        },
        goBack() {
            this.expandPanel({ panel: 'main' })
            this.$router.push({ name: 'geofleet.index' })
        },
        async resetConfig() {
            this.$store.commit('setTimedLoading', 500)
            const ok = await this.$store.dispatch('config/reset')
            if (ok) {
                this.$snotify.success(this.__('reset_settings_success'), { position: 'centerTop' })
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@use '@/components/panels/SidePanel.scss';

/* Fill the full panel-box height and take all available width */
#settings-panel {
    flex: 1;
    min-width: 0;
    align-self: stretch;
}

.a-header {
    display: flex;
    align-items: center;
}

.settings-wrapper {
    padding: 16px;
    height: 100%;
    overflow-y: auto;
}

/* Match the width behaviour from the original SettingsPanel */
#settings-panel ::v-deep(.head),
#settings-panel ::v-deep(.body) {
    --side-panels-security-width: 100%;
    width: var(--side-panels-security-width);
}
</style>
